import { DialogTitle } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Button from "components/buttons/Button";
import BusyPeriodListItem from "pages/out-of-office/BusyPeriodListItem";
import { SupplierBusyPeriod } from "pages/out-of-office/out-of-office.typing";
import { useOutOfOfficeListView } from "pages/out-of-office/useOutOfOfficeListView";
import { TreePalm } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/ui";

interface Props {
  periods: SupplierBusyPeriod[];
  isLoading?: boolean;
  onAdd: () => void;
  onEdit: (period: SupplierBusyPeriod) => void;
  onDelete: (id: string) => void;
}

const OutOfOfficeListView = ({ periods, isLoading = false, onAdd, onEdit, onDelete }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "outOfOffice.list" });
  const { isEmpty, periodCount } = useOutOfOfficeListView(periods);
  const showEmpty = !isLoading && isEmpty;

  return (
    <div className="flex flex-col" data-test="out-of-office-list-view">
      <div className="pr-8 text-center sm:text-left">
        <DialogTitle
          as="h3"
          className="text-color-primary inline-flex items-center justify-center gap-2 text-lg font-medium leading-6 sm:justify-start sm:text-2xl"
        >
          {t("title")}
          <TreePalm className="size-6 shrink-0 text-ribbon-500 sm:size-7" aria-hidden="true" />
        </DialogTitle>
        <p className="text-color-secondary mt-2 text-sm">{t("subtitle")}</p>
      </div>

      {isLoading ? (
        <p className="text-color-secondary mt-6 text-sm" data-test="busy-periods-loading">
          {t("loading")}
        </p>
      ) : null}

      {!isLoading && !isEmpty ? (
        <div className="mt-6">
          <h4 className="text-color-secondary text-xs font-semibold uppercase tracking-wide">
            {t("upcomingCurrent", { count: periodCount })}
          </h4>
          <ul className="mt-3 space-y-3">
            {periods.map((period) => (
              <li key={period.id}>
                <BusyPeriodListItem period={period} onEdit={onEdit} onDelete={onDelete} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={cn("mt-6", showEmpty ? "w-full" : "flex justify-end")}>
        <Button
          type="button"
          color="primary"
          onClick={onAdd}
          disabled={isLoading}
          className={cn(showEmpty && "w-full")}
          dataTestId="button-add-busy-period"
        >
          <PlusIcon className="-ml-1 mr-2 size-5" aria-hidden="true" />
          {t("addNewPeriod")}
        </Button>
      </div>
    </div>
  );
};

export default OutOfOfficeListView;
